import React from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { Trophy, Gem, RotateCcw, ChevronLeft, Frown } from "lucide-react";

export interface GameResultsScreenProps {
  sessionCrystals: number;
  onPlayAgain: () => void;
  onBackToDeck: () => void;
  /** Optional header row (e.g. back button + volume) to show above the card. */
  headerRow?: React.ReactNode;
  /** 'timeOut' = time ran out (loss); default 'victory' = win. */
  variant?: "victory" | "timeOut";
}

/**
 * Premium Results Screen shown when the deck/levels are completely finished.
 * Used by IqMode, MemoryJumbleMode, MemoryMixMode, CipherRebusMode, BlitzRushMode.
 */
export function GameResultsScreen({
  sessionCrystals,
  onPlayAgain,
  onBackToDeck,
  headerRow,
  variant = "victory",
}: GameResultsScreenProps) {
  const { t } = useTranslation();
  const isTimeOut = variant === "timeOut";

  return (
    <div className="flex flex-col w-full max-w-lg mx-auto px-4 pb-10">
      {headerRow}
      <motion.div
        initial={{ opacity: 0, y: 20, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ type: "spring", stiffness: 300, damping: 28 }}
        className="relative overflow-hidden rounded-2xl border-2 border-[hsl(var(--border))] bg-white/70 dark:bg-slate-900/70 backdrop-blur-md shadow-xl p-8 sm:p-10 text-center"
      >
        <div
          className="absolute inset-0 opacity-30 pointer-events-none"
          style={{
            background:
              "linear-gradient(135deg, rgba(168,85,247,0.08) 0%, rgba(236,72,153,0.08) 50%, rgba(249,115,22,0.08) 100%)",
          }}
        />
        {/* Decorative icon: no TTS/hover handlers; pointer-events-none prevents event leak */}
        <div className="pointer-events-none relative z-10" aria-hidden>
          {isTimeOut ? (
            <Frown
              className="w-14 h-14 sm:w-16 sm:h-16 text-blue-400 dark:text-blue-400 mx-auto mb-5"
              strokeWidth={2}
              aria-hidden
            />
          ) : (
            <Trophy
              className="w-14 h-14 sm:w-16 sm:h-16 text-amber-500 dark:text-amber-400 mx-auto mb-5"
              strokeWidth={2}
              aria-hidden
            />
          )}
        </div>
        <h2 className="text-2xl sm:text-3xl font-bold text-[hsl(var(--foreground))] mb-2 relative z-10">
          {isTimeOut
            ? t("gameResults.timeUpTitle", { defaultValue: "Time's Up!" })
            : t("gameResults.congratulations", { defaultValue: "Congratulations!" })}
        </h2>
        <p className="text-sm text-[hsl(var(--muted-foreground))] mb-8 relative z-10">
          {isTimeOut
            ? t("gameResults.timeUpSubtitle", {
                defaultValue:
                  "Don't worry! With every try you get faster. Play again?",
              })
            : t("gameResults.gameCompleteHint", {
                defaultValue: "You completed the deck!",
              })}
        </p>
        <div className="inline-flex items-center justify-center gap-2 rounded-xl bg-[hsl(var(--muted)/0.5)] dark:bg-[hsl(var(--muted)/0.3)] border border-[hsl(var(--border))] px-6 py-4 mb-8 relative z-10">
          <Gem className="w-7 h-7 text-[hsl(var(--accent2))]" aria-hidden />
          <span className="text-2xl font-bold text-[hsl(var(--foreground))]">
            {sessionCrystals}
          </span>
          <span className="text-sm font-medium text-[hsl(var(--muted-foreground))]">
            {t("gameResults.crystalsEarned", { defaultValue: "crystals earned" })}
          </span>
        </div>
        <div className="flex flex-col sm:flex-row gap-3 justify-center relative z-10">
          <button
            type="button"
            onClick={onPlayAgain}
            className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-semibold bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] hover:opacity-90 transition-opacity"
          >
            <RotateCcw className="w-5 h-5 shrink-0" aria-hidden />
            {t("gameResults.playAgain", { defaultValue: "Play Again" })}
          </button>
          <button
            type="button"
            onClick={onBackToDeck}
            className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-semibold border-2 border-[hsl(var(--border))] bg-[hsl(var(--card-bg))] text-slate-800 dark:text-slate-200 hover:bg-[hsl(var(--bg3))] transition-colors"
          >
            <ChevronLeft className="w-4 h-4 shrink-0" aria-hidden />
            {t("studyMenu.backToDeck", { defaultValue: "Back to deck" })}
          </button>
        </div>
      </motion.div>
    </div>
  );
}
